import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { getDueDetail, createPaymentOrder, verifyPayment } from '../../../api/paymentTransactions/dues';
import { initiatePayment } from '../../../lib/razorpay';
import '../PaymentTransactions.css';

interface DueDetail {
  _id: string;
  tournamentName: string;
  team: string;
  ageCategory: string;
  players: number;
  feePerPlayer: number;
  subtotal: number;
  gst: number;
  total: number;
  minimumPayment: number;
  amountPaid: number;
  pendingPayment: number;
  dueDate: string;
  status: 'Due' | 'Overdue' | 'Paid';
  upcomingPayments?: {
    installment: string;
    amount: number;
    dueDate: string;
    status: 'Due' | 'Overdue' | 'Paid';
  }[];
}

const DueDetail: React.FC = () => {
  const navigate = useNavigate();
  const { dueId } = useParams<{ dueId: string }>();
  const location = useLocation();
  
  const [dueDetail, setDueDetail] = useState<DueDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Mock data for development
  const mockDueDetail: DueDetail = {
    _id: dueId || '1',
    tournamentName: 'SFL Football League',
    team: 'Silver Wolves',
    ageCategory: 'U17',
    players: 19,
    feePerPlayer: 1000,
    subtotal: 19000,
    gst: 1520,
    total: 20520,
    minimumPayment: 4750,
    amountPaid: 4750,
    pendingPayment: 15770,
    dueDate: '2025-07-30',
    status: 'Overdue',
    upcomingPayments: [
      {
        installment: 'SECOND PAYMENT: 50% OF TOTAL',
        amount: 15770,
        dueDate: '2025-07-30',
        status: 'Overdue'
      }
    ]
  };

  useEffect(() => {
    loadDueDetail();
  }, [dueId]);

  const loadDueDetail = async () => {
    try {
      setLoading(true);
      // In real implementation, call the API
      // const response = await getDueDetail(dueId);
      // setDueDetail(response.data);
      
      // For now, use mock data
      setTimeout(() => {
        const detail = location.state?.due ? {
          ...mockDueDetail,
          ...location.state.due
        } : mockDueDetail;
        setDueDetail(detail);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error loading due detail:', error);
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!dueDetail) return;
    
    setProcessingPayment(true);
    try {
      // Create Razorpay order
      const orderResponse = await createPaymentOrder(dueDetail._id, dueDetail.pendingPayment);
      
      // Initiate payment with Razorpay
      await initiatePayment(
        dueDetail.pendingPayment,
        orderResponse.orderId,
        `Payment for ${dueDetail.tournamentName} - ${dueDetail.team}`,
        {
          name: 'User Name', // In real app, get from user context
          email: 'user@example.com', // In real app, get from user context
          phone: '1234567890' // In real app, get from user context
        },
        async (response) => {
          // Payment successful
          try {
            // Verify payment with backend
            const verificationResult = await verifyPayment(dueDetail._id, response);
            
            if (verificationResult.success) {
              alert('Payment successful!');
              // Refresh the page or update the UI
              window.location.reload();
            } else {
              alert('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment verification failed. Please contact support.');
          }
        },
        (error: any) => {
          console.error('Payment failed:', error);
          
          // Enhanced error handling with text mode
          if (error && error.text) {
            alert(error.text);
            console.log('Payment failure details:', error);
          } else {
            alert('Payment failed. Please try again.');
          }
          setProcessingPayment(false);
        },
        () => {
          console.log('Payment modal dismissed');
          setProcessingPayment(false);
        }
      );
      
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleContactAdmin = () => {
    // In real implementation, this could open a contact form or redirect to support
    alert('Contact admin functionality would be implemented here.');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="due-detail-page">
        <Container fluid>
          <ShimmerLoader type="due-detail" />
        </Container>
      </div>
    );
  }

  if (!dueDetail) {
    return (
      <div className="due-detail-page">
        <Container fluid>
          <div className="text-center py-4">
            <h5>Payment detail not found</h5>
            <Button variant="primary" onClick={() => navigate('/payment-transactions/dues')}>
              Back to Dues
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const daysUntilDue = getDaysUntilDue(dueDetail.dueDate);
  const isOverdue = daysUntilDue < 0;
  const isDueSoon = daysUntilDue <= 5 && daysUntilDue >= 0;

  return (
    <div className="due-detail-page">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              {
                label: 'Payment & Transactions',
                onClick: () => navigate('/payment-transactions')
              },
              {
                label: 'Dues',
                onClick: () => navigate('/payment-transactions/dues')
              },
              {
                label: 'Payment Details',
                active: true
              }
            ]} 
          />
        </div>

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4" style={{ padding: '0 15px' }}>
          <div>
            <h4 className="mb-2">{dueDetail.tournamentName}: Payment Details</h4>
            <p className="text-muted mb-0">Review and complete your payment</p>
          </div>
          <Button 
            variant="outline-dark"
            size="sm"
            onClick={() => window.print()}
          >
            <i className="ph-download me-1"></i>
            Download Invoice
          </Button>
        </div>

        {/* Bill Details */}
        <Row>
          <Col md={12}>
            <Card className="mb-4">
              <Card.Body>
                <h5 className="card-title mb-3">{dueDetail.team} Bill</h5>
                <p className="text-muted mb-4">Pay your bills quickly and securely.</p>
                
                <div className="bill-breakdown">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Number of Players</span>
                    <span>{dueDetail.players}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Fee per Player</span>
                    <span>{formatCurrency(dueDetail.feePerPlayer)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Subtotal</span>
                    <span>{formatCurrency(dueDetail.subtotal)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span>GST 8%</span>
                    <span>{formatCurrency(dueDetail.gst)}</span>
                  </div>
                  
                  <hr />
                  
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Total</strong>
                    <strong className="text-primary">{formatCurrency(dueDetail.total)}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-primary">Minimum: 25% Payment Initially</span>
                    <span className="text-primary">{formatCurrency(dueDetail.minimumPayment)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Amount Paid</strong>
                    <strong className="text-primary">{formatCurrency(dueDetail.amountPaid)}</strong>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <strong>Pending Payment</strong>
                    <strong>{formatCurrency(dueDetail.pendingPayment)}</strong>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Upcoming Payments */}
        <Row>
          <Col md={12}>
            <Card className="mb-4" style={{ 
              backgroundColor: isOverdue ? '#fff5f5' : isDueSoon ? '#fffbf0' : '#f8f9fa',
              borderColor: isOverdue ? '#fecaca' : isDueSoon ? '#fed7aa' : '#e9ecef'
            }}>
              <Card.Body>
                <h6 className="card-title mb-3">Upcoming Payments</h6>
                
                {dueDetail.upcomingPayments?.map((payment, index) => (
                  <div key={index} className="upcoming-payment">
                    {isOverdue && (
                      <div className="d-flex align-items-center mb-2">
                        <i className="ph-warning-triangle text-danger me-2"></i>
                        <span className="text-danger fw-bold">PAYMENT OVERDUE</span>
                      </div>
                    )}
                    {isDueSoon && !isOverdue && (
                      <div className="d-flex align-items-center mb-2">
                        <i className="ph-warning-triangle text-warning me-2"></i>
                        <span className="text-warning fw-bold">PAYMENT DUE IN {daysUntilDue} DAYS</span>
                      </div>
                    )}
                    
                    <div className="mb-2">
                      <strong>{payment.installment}</strong>
                    </div>
                    <div className="mb-3">
                      <span className="text-muted">Due on: {formatDate(payment.dueDate)}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="h5 mb-0">{formatCurrency(payment.amount)}</span>
                    </div>
                    
                    <div className="d-flex gap-2">
                      <Button 
                        variant="outline-dark" 
                        size="sm"
                        onClick={handleContactAdmin}
                        className="flex-fill"
                      >
                        Contact Admin
                      </Button>
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={handlePayNow}
                        disabled={processingPayment}
                        className="flex-fill"
                      >
                        {processingPayment ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Processing...
                          </>
                        ) : (
                          'Pay Now'
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DueDetail; 